import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { CustomMarkdown } from "../../custom-markdown/custom-markdown";
import { AuthenticatedImage } from "../../authenticated-media/authenticated-image";
import { Message, MessagePrev } from "@/types";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { formatDate } from "../../../helpers/dateUtils";
import {
  getAudioDisplayText,
  getVideoDisplayText,
} from "../../../helpers/messageHelpers";
import { useInfoPanelListRowHelpers } from "../../../hooks/useInfoPanelListRowHelpers";
import {
  containerListStyles,
  messageBoxStyles,
  markdownBoxStyles,
  imageCollectionBoxStyles,
  textWrapperStyles,
  messageFooterStyles,
  lessonBadgeStyles,
} from "./styles";

interface FavoriteMessagesPanelProps {
  favoriteMessages: MessagePrev[];
  isLoading: boolean;
  error: string | null;
  onMessageClick: (messageId: string, domain: string, focus: string) => void;
  lessons?: MessagePrev[];
  messages?: Message[];
  crashCourses?: MessagePrev[];
}

export const FavoriteMessagesPanel: React.FC<FavoriteMessagesPanelProps> = ({
  favoriteMessages,
  isLoading,
  error,
  onMessageClick,
  lessons = [],
  messages = [],
  crashCourses = [],
}) => {
  const theme = useTheme();
  const { getTabbedText, getBadgeLabel } = useInfoPanelListRowHelpers({
    panelRows: favoriteMessages,
    messages,
    lessons,
    crashCourses,
  });

  const renderFavoriteMessagesList = (items: MessagePrev[]) => {
    if (!items || items.length === 0) {
      return <Typography>No favorite messages available</Typography>;
    }

    return (
      <Box sx={containerListStyles}>
        {items.map((msg) => (
          <Box
            key={msg.message_id}
            onClick={() =>
              onMessageClick(msg.message_id, msg.domain, msg.focus)
            }
            sx={messageBoxStyles}
          >
            {msg.type === "image" && msg.file_url ? (
              <AuthenticatedImage
                src={msg.file_url}
                alt="Image message"
                sx={{ width: "100%", borderRadius: 2, marginBottom: "5px" }}
              />
            ) : (msg.type === "image_collection" ||
                msg.type === "flash_card_image_collection") &&
              msg.file_url ? (
              <Box sx={imageCollectionBoxStyles}>
                <AuthenticatedImage
                  src={msg.file_url}
                  alt="Image collection messsage"
                  sx={{
                    maxWidth: "40%",
                    flexShrink: 0,
                    borderRadius: 2,
                    marginBottom: "5px",
                  }}
                />
                <Box sx={textWrapperStyles}>
                  <CustomMarkdown>{msg.text}</CustomMarkdown>
                </Box>
              </Box>
            ) : msg.type === "tabbed_message" || msg.tabbed_content?.length ? (
              (() => {
                return (
                  <Box>
                    <Box sx={markdownBoxStyles}>
                      <CustomMarkdown>{getTabbedText(msg)}</CustomMarkdown>
                    </Box>
                  </Box>
                );
              })()
            ) : (
              <Box sx={markdownBoxStyles}>
                <CustomMarkdown>
                  {msg.type === "audio"
                    ? getAudioDisplayText(msg)
                    : msg.type === "video"
                      ? getVideoDisplayText(msg)
                      : msg.text}
                </CustomMarkdown>
              </Box>
            )}
            <Box sx={messageFooterStyles}>
              <CustomTypography
                fontWeight={500}
                fontSize="12px"
                color={theme.palette.panelInfo.message.timeColor}
              >
                {formatDate(msg.date)}
              </CustomTypography>
              <Typography sx={lessonBadgeStyles}>{getBadgeLabel(msg)}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return <Typography>Loading...</Typography>;
    }
    if (error) {
      return <Typography color="error">{error}</Typography>;
    }
    return renderFavoriteMessagesList(favoriteMessages);
  };

  return <Box>{renderContent()}</Box>;
};
